/**
 * Renders a structured ``ErrorInfo`` from the agentic-backend so the
 * user can immediately tell whether a node failure is:
 *
 * * an external service problem (LLM provider outage, rate-limit,
 *   timeout) -- which they can do little about beyond waiting,
 * * a configuration problem (invalid API key, exhausted quota,
 *   missing permissions) -- which their admin needs to fix,
 * * a user-input problem (content policy, context length, malformed
 *   parameters) -- which they can fix by editing the node, or
 * * an internal/unknown problem -- which they should report to
 *   support with the run ID.
 *
 * The banner colour, icon, and primary CTA all come from the
 * structured ``ErrorCategory``; the precise message comes from
 * ``user_message`` (rendered by the backend, source of truth in
 * ``temporal/errors/error_info.py``).
 *
 * Falls back to a generic "Unknown error" banner if ``info`` is
 * missing any required field, so a stale FE deployed against a newer
 * BE never crashes -- it just shows less detail.
 */
import React, { useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Clock,
  ExternalLink,
  Info,
  Lock,
  ServerCrash,
  ShieldAlert,
  Wallet,
} from 'lucide-react';

import {
  ErrorCategory,
  ErrorCode,
  ErrorInfo,
  ErrorProvider,
} from '@/types/workflow-types';

interface ExternalApiErrorBannerProps {
  info: ErrorInfo;
  /**
   * Run ID to surface in the support callout.  Useful when the error
   * is internal/unknown -- gives the user something to copy into a
   * support ticket.
   */
  runId?: string;
  /**
   * Compact rendering -- single-line title + collapsed details.
   * Used inside small panels (e.g. node preview) where the full
   * banner would dominate.
   */
  compact?: boolean;
}

interface BannerStyle {
  containerClass: string;
  iconClass: string;
  Icon: React.ComponentType<{ className?: string }>;
  title: string;
}

/**
 * Map each ErrorCategory to its visual treatment + headline.
 *
 * Colour intent:
 *   amber  -- transient external issue, "we'll retry / try again"
 *   red    -- something the user / admin needs to fix
 *   slate  -- internal issue, "contact support"
 *
 * Specific ``ErrorCode`` overrides take precedence (see
 * ``codeOverrides`` below) for higher-impact failures like
 * QUOTA_EXCEEDED / AUTH_INVALID where the category alone undersells
 * the urgency.
 */
const STYLE_BY_CATEGORY: Record<string, BannerStyle> = {
  [ErrorCategory.EXTERNAL_SERVICE]: {
    containerClass:
      'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100',
    iconClass: 'text-amber-600 dark:text-amber-400',
    Icon: ServerCrash,
    title: 'External service issue',
  },
  [ErrorCategory.RATE_LIMIT]: {
    containerClass:
      'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100',
    iconClass: 'text-amber-600 dark:text-amber-400',
    Icon: Clock,
    title: 'Provider rate limit',
  },
  [ErrorCategory.USER_INPUT]: {
    containerClass:
      'border-red-200 bg-red-50 text-red-900 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-100',
    iconClass: 'text-red-600 dark:text-red-400',
    Icon: AlertTriangle,
    title: 'Input needs adjustment',
  },
  [ErrorCategory.CONFIGURATION]: {
    containerClass:
      'border-red-200 bg-red-50 text-red-900 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-100',
    iconClass: 'text-red-600 dark:text-red-400',
    Icon: Lock,
    title: 'Configuration issue',
  },
  [ErrorCategory.INTERNAL]: {
    containerClass:
      'border-slate-200 bg-slate-50 text-slate-900 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-100',
    iconClass: 'text-slate-600 dark:text-slate-400',
    Icon: AlertCircle,
    title: 'Unexpected error',
  },
  [ErrorCategory.CANCELLED]: {
    containerClass:
      'border-slate-200 bg-slate-50 text-slate-900 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-100',
    iconClass: 'text-slate-600 dark:text-slate-400',
    Icon: Info,
    title: 'Cancelled',
  },
};

/** Per-code overrides where the category style is too generic. */
const CODE_OVERRIDES: Partial<Record<string, Partial<BannerStyle>>> = {
  [ErrorCode.QUOTA_EXCEEDED]: {
    Icon: Wallet,
    title: 'Provider account out of credits',
  },
  [ErrorCode.AUTH_INVALID]: {
    Icon: ShieldAlert,
    title: 'Provider authentication failed',
  },
  [ErrorCode.PERMISSION_DENIED]: {
    Icon: ShieldAlert,
    title: 'Insufficient provider permissions',
  },
};

const FALLBACK_STYLE: BannerStyle = STYLE_BY_CATEGORY[ErrorCategory.INTERNAL];

/** Pretty-print the provider enum for the badge. */
const PROVIDER_LABEL: Record<string, string> = {
  [ErrorProvider.ANTHROPIC]: 'Anthropic',
  [ErrorProvider.OPENAI]: 'OpenAI',
  [ErrorProvider.OPENROUTER]: 'OpenRouter',
  [ErrorProvider.GEMINI]: 'Google Gemini',
  [ErrorProvider.PYDANTIC_AI]: 'pydantic-ai',
  [ErrorProvider.PLATFORM_API]: 'Platform API',
};

function styleForInfo(info: ErrorInfo): BannerStyle {
  const baseStyle = STYLE_BY_CATEGORY[info.category] ?? FALLBACK_STYLE;
  const override = CODE_OVERRIDES[info.code];
  return override ? { ...baseStyle, ...override } : baseStyle;
}

function providerLabel(provider: ErrorInfo['provider']): string | null {
  if (provider === ErrorProvider.UNKNOWN) {
    return null;
  }
  return PROVIDER_LABEL[provider] ?? null;
}

export const ExternalApiErrorBanner: React.FC<ExternalApiErrorBannerProps> = ({
  info,
  runId,
  compact = false,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const style = styleForInfo(info);
  const { Icon } = style;
  const provider = providerLabel(info.provider);

  // Show "After N attempts" only when retries actually happened.
  // For non-retryable errors we tried once and stopped -- saying "1
  // attempt" is noise.
  const showAttemptCount = info.retryable && info.attempts_made > 1;

  return (
    <div
      className={`rounded-md border px-4 py-3 ${style.containerClass}`}
      role='alert'
      data-error-code={info.code}
      data-error-category={info.category}
    >
      <div className='flex items-start gap-3'>
        <Icon className={`size-5 shrink-0 mt-0.5 ${style.iconClass}`} />
        <div className='min-w-0 flex-1'>
          <div className='flex flex-wrap items-baseline gap-x-2'>
            <h4 className='text-sm font-semibold leading-tight'>
              {style.title}
            </h4>
            {provider && (
              <span className='text-xs font-medium opacity-80'>
                · {provider}
              </span>
            )}
            {showAttemptCount && (
              <span className='text-xs font-medium opacity-80'>
                · {info.attempts_made} attempts
              </span>
            )}
          </div>
          <p className='mt-1 text-sm leading-snug'>{info.user_message}</p>
          {info.actionable_hint && (
            <p className='mt-1 text-xs leading-snug opacity-80'>
              {info.actionable_hint}
            </p>
          )}

          {!compact && (
            <button
              type='button'
              onClick={() => setShowDetails((v) => !v)}
              className='mt-2 inline-flex items-center gap-1 text-xs font-medium underline-offset-2 hover:underline opacity-75 hover:opacity-100'
              aria-expanded={showDetails}
            >
              {showDetails
                ? 'Hide technical details'
                : 'Show technical details'}
            </button>
          )}

          {!compact && showDetails && (
            <dl className='mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs'>
              <dt className='opacity-70'>Code</dt>
              <dd className='font-mono'>{info.code}</dd>
              <dt className='opacity-70'>Category</dt>
              <dd className='font-mono'>{info.category}</dd>
              {info.http_status != null && (
                <>
                  <dt className='opacity-70'>HTTP</dt>
                  <dd className='font-mono'>{info.http_status}</dd>
                </>
              )}
              {info.request_id && (
                <>
                  <dt className='opacity-70'>Provider request ID</dt>
                  <dd className='font-mono break-all'>{info.request_id}</dd>
                </>
              )}
              {runId && (
                <>
                  <dt className='opacity-70'>Run ID</dt>
                  <dd className='font-mono break-all'>{runId}</dd>
                </>
              )}
              <dt className='opacity-70 self-start'>Original message</dt>
              <dd className='font-mono break-words whitespace-pre-wrap'>
                {info.technical_message}
              </dd>
            </dl>
          )}

          {/* For external-service / rate-limit errors, link to the
           * provider's status page so the user can verify it's
           * actually them and not us.  Static link list -- no need
           * for a fancy lookup table at this point. */}
          {!compact && isStatusPageRelevant(info) && (
            <div className='mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs'>
              {statusPageLink(info.provider) && (
                <a
                  href={statusPageLink(info.provider)!}
                  target='_blank'
                  rel='noreferrer'
                  className='inline-flex items-center gap-1 underline underline-offset-2 hover:no-underline'
                >
                  Provider status page
                  <ExternalLink className='size-3' />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function isStatusPageRelevant(info: ErrorInfo): boolean {
  return (
    info.category === ErrorCategory.EXTERNAL_SERVICE ||
    info.category === ErrorCategory.RATE_LIMIT
  );
}

function statusPageLink(provider: ErrorInfo['provider']): string | null {
  switch (provider) {
    case ErrorProvider.ANTHROPIC:
      return 'https://status.anthropic.com';
    case ErrorProvider.OPENAI:
      return 'https://status.openai.com';
    case ErrorProvider.OPENROUTER:
      return 'https://status.openrouter.ai';
    case ErrorProvider.GEMINI:
      return 'https://status.cloud.google.com';
    default:
      return null;
  }
}
